import React, { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createTheme,
  CSSVariablesResolver,
  MantineProvider,
  rem,
} from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { hsbiColors } from "./layout/colors.ts";
import "./layout/fonts.css";
import "./layout/styles.css";
/**
 * Die Devtools zeigen den kompletten Query-Cache — im Bewerbungsportal also
 * Namen, Anschriften und Bewertungen. Sie gehoeren nicht ins Produktionsbundle,
 * weder sichtbar noch als toter Code. Der dynamische Import laesst Vite den
 * Zweig beim Build entfernen, weil import.meta.env.DEV dort statisch false ist.
 */
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(async () => {
      const modul = await import("@tanstack/react-query-devtools");
      return { default: modul.ReactQueryDevtools };
    })
  : null;
import { RouterProvider } from "react-router-dom";
import { routes } from "./routes.tsx";
import { UserData } from "./UserData.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 60 * 1000,
    },
  },
});

const theme = createTheme({
  fontFamily: "Lausanne, Arial, sans-serif",
  colors: {
    ...hsbiColors,
    red: hsbiColors.rot,
    blue: hsbiColors.blau,
    green: hsbiColors.gruen,
  },
  headings: {
    textWrap: "balance",
    sizes: {},
  },
  autoContrast: true,
  defaultRadius: 0,
  respectReducedMotion: true,
  fontSizes: {
    xs: rem(14),
    sm: rem(16),
    md: rem(18),
    lg: rem(20),
    xl: rem(24),
  },
});

/**
 * Die Seitenflaeche ist sand-1 (#F3F1ED), nicht Weiss. Mantines Standardwerte
 * sind gegen Weiss gerechnet und verlieren auf diesem Untergrund gerade so viel
 * Kontrast, dass sie unter 4,5:1 fallen:
 *
 * - `dimmed` (gray.6) lag bei 2,94:1 — und traegt an dreizehn Stellen
 *   Erlaeuterungen, Zeitangaben und Zaehler.
 * - Die Outline-Variante nutzt eine eigene Variable, nicht `-filled`. Die
 *   Filter der Kommissionsliste lagen deshalb weiter bei 2,72:1, obwohl die
 *   gefuellte Variante schon korrigiert war.
 *
 * Beides hier zu setzen wirkt an allen Stellen zugleich — pro Seite nachzubessern
 * haette dieselbe Luecke bei jedem neuen Knopf wieder aufgerissen.
 */
const resolver: CSSVariablesResolver = (theme) => ({
  variables: {},
  light: {
    "--mantine-color-anchor": theme.colors.blue[9],
    "--mantine-color-blue-filled": theme.colors.blue[8],
    "--mantine-color-blue-filled-hover": theme.colors.blue[9],
    "--mantine-color-blue-outline": theme.colors.blue[9],
    "--mantine-color-blue-outline-hover": theme.colors.blue[0],
    "--mantine-color-dimmed": theme.colors.grau[9],
  },
  dark: {},
});

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Element #root nicht gefunden");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} cssVariablesResolver={resolver}>
        <UserData>
          <RouterProvider router={routes} />
          {ReactQueryDevtools && (
            <Suspense fallback={null}>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          )}
        </UserData>
      </MantineProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
